import { useEffect, useState } from "react";
import { Hand } from "lucide-react";
import type { BannerAlign } from "./pin-direction-hint.helpers";

/** Shared coachmark copy — one source of truth so the three set-direction
 *  surfaces (plan-viewer ghost pin, expanded-plan-view, modal thumbnail) never
 *  drift apart. See [[directional-pins]] and [[pin-direction-hint.spec]]. */
export const PIN_DIRECTION_HINT_TEXT =
  "Drag the arrow to set which way you were facing";

/** How long the coachmark lingers before it fades and unmounts. */
const HINT_DURATION_MS = 5000;

/** Angular span (deg, measured clockwise from the rest direction) over which the
 *  flanking rotation arrows are drawn. The arrows bracket the knob on each side
 *  to say "swing this either way". */
const ARROW_INNER_DEG = 18;
const ARROW_OUTER_DEG = 64;
/** Arrowhead length in local px (rides `--pin-scale` with everything else). */
const ARROW_HEAD = 11;
/** Padding around the pin circle so arrowheads aren't clipped by the SVG box. */
const SVG_PAD = 26;

export interface PinDirectionHintProps {
  /** Pin location in image-relative coords `[0,1]` — the hint pivots here,
   *  identical to {@link PinRotateHandle}. */
  xPct: number;
  yPct: number;
  /**
   * On-screen radius (px at `--pin-scale: 1`) from the pin to the knob — pass
   * the same value the rotate handle uses so the arrows ride the real arc.
   */
  radiusPx: number;
  /** Bearing the arc is centred on when no direction is set yet. Default 0 (top). */
  restBearing?: number;
  /** Auto-dismiss window in ms. Defaults to {@link HINT_DURATION_MS}. */
  durationMs?: number;
}

/** Point on a circle of radius `r` at angle `theta` (deg, clockwise from top),
 *  in the SVG's local space whose centre `c` sits on the pin. */
function pointAt(c: number, r: number, thetaDeg: number): [number, number] {
  const t = (thetaDeg * Math.PI) / 180;
  return [c + r * Math.sin(t), c - r * Math.cos(t)];
}

/** Builds a curved arrow (arc + filled arrowhead) sweeping from `thetaA` to
 *  `thetaB` at radius `r`, with the head at `thetaB` pointing along travel. */
function curvedArrow(
  c: number,
  r: number,
  thetaA: number,
  thetaB: number,
): { arc: string; head: string } {
  const [x1, y1] = pointAt(c, r, thetaA);
  const [x2, y2] = pointAt(c, r, thetaB);
  const sweep = thetaB > thetaA ? 1 : 0;
  const dir = thetaB > thetaA ? 1 : -1;
  // Tangent at the head (travel direction) and the radial axis at thetaB.
  const tan = ((thetaB + dir * 90) * Math.PI) / 180;
  const tx = Math.sin(tan);
  const ty = -Math.cos(tan);
  const rad = (thetaB * Math.PI) / 180;
  const px = Math.sin(rad);
  const py = -Math.cos(rad);
  const baseX = x2 - tx * ARROW_HEAD;
  const baseY = y2 - ty * ARROW_HEAD;
  const w = ARROW_HEAD * 0.6;
  return {
    arc: `M ${x1} ${y1} A ${r} ${r} 0 0 ${sweep} ${x2} ${y2}`,
    head: `M ${x2} ${y2} L ${baseX + px * w} ${baseY + py * w} L ${baseX - px * w} ${baseY - py * w} Z`,
  };
}

/**
 * A transient "drag to aim" coachmark: twin curved rotation arrows flank the
 * pin's rotate knob — saying "swing this either way to aim" — with a faded open
 * hand bobbing along the arc between them. It teaches the drag-to-set-direction
 * gesture, which is otherwise easy to miss (the rotate handle is discoverable
 * but silent). It is purely decorative — `pointer-events-none` throughout, sets
 * no bearing, and never intercepts the real handle underneath.
 *
 * It anchors exactly like {@link PinRotateHandle} (pivot at the pin, riding
 * `--pin-scale` for a constant on-screen size), so the arrows trace the same
 * orbit the user is being taught to drag. The arrows pulse; an inner arm bobs
 * the hand a few degrees about the pin and the hand counter-rotates to stay
 * upright while it travels. Respects `prefers-reduced-motion` (the bob and pulse
 * stop, leaving a still hint that simply fades).
 *
 * Self-dismissing: it shows on mount and unmounts after `durationMs`. Callers
 * gate it on "edit mode + pin placed + no bearing yet" and give it a `key` that
 * changes per placement so it replays each time a fresh pin appears. Once the
 * user starts dragging, the bearing becomes non-null and the caller unmounts it.
 *
 * See [[pin-direction-hint.spec]] and [[directional-pins]].
 */
export function PinDirectionHint({
  xPct,
  yPct,
  radiusPx,
  restBearing = 0,
  durationMs = HINT_DURATION_MS,
}: PinDirectionHintProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const id = window.setTimeout(() => setVisible(false), durationMs);
    return () => window.clearTimeout(id);
  }, [durationMs]);

  if (!visible) return null;

  // SVG local box: a square just big enough to hold the pin circle + arrowheads,
  // centred on the pin so (c, c) maps to the pin point (0,0).
  const c = radiusPx + SVG_PAD;
  const box = c * 2;
  const right = curvedArrow(c, radiusPx, ARROW_INNER_DEG, ARROW_OUTER_DEG);
  const left = curvedArrow(c, radiusPx, -ARROW_INNER_DEG, -ARROW_OUTER_DEG);

  return (
    <div
      aria-hidden
      className="pin-aim-hint pointer-events-none absolute"
      style={{
        left: `${xPct * 100}%`,
        top: `${yPct * 100}%`,
        width: 0,
        height: 0,
        // The opacity envelope (fade in, hold, fade out) spans the full lifetime.
        animationDuration: `${durationMs}ms`,
        transform: `rotate(${restBearing}deg) scale(var(--pin-scale, 1))`,
        transformOrigin: "0 0",
      }}
    >
      {/* Twin rotation arrows — the primary cue. Static arcs flanking the knob
          on each side; they pulse to draw the eye. */}
      <svg
        className="pin-aim-hint-arrows absolute overflow-visible text-tertiary"
        style={{ left: -c, top: -c }}
        width={box}
        height={box}
        viewBox={`0 0 ${box} ${box}`}
        fill="none"
      >
        <path
          d={right.arc}
          stroke="currentColor"
          strokeWidth={4}
          strokeLinecap="round"
        />
        <path d={right.head} fill="currentColor" />
        <path
          d={left.arc}
          stroke="currentColor"
          strokeWidth={4}
          strokeLinecap="round"
        />
        <path d={left.head} fill="currentColor" />
      </svg>

      {/* Faded hand — secondary. The arm bobs it along the arc; the hand
          counter-rotates so it stays upright instead of tumbling. The centring
          translate lives in the keyframe (and the base transform) so the bob's
          `transform` can't clobber it. */}
      <div
        className="pin-aim-hint-arm absolute"
        style={{ left: 0, top: 0, width: 0, height: 0, transformOrigin: "0 0" }}
      >
        <div
          className="pin-aim-hint-finger absolute"
          style={{
            left: 0,
            top: -radiusPx,
            opacity: 0.3,
            transform: "translate(-50%, -50%)",
            transformOrigin: "50% 50%",
          }}
        >
          <Hand
            className="h-6 w-6 text-tertiary drop-shadow"
            strokeWidth={2.25}
            fill="white"
          />
        </div>
      </div>
    </div>
  );
}

/** On-screen gap (px at `--pin-scale: 1`) between the pin and the near edge of
 *  the banner pill, clearing the coachmark/cone that sits above the pin. */
const BANNER_GAP_PX = 34;

export interface PinDirectionHintBannerProps {
  /** Pin location in image-relative coords `[0,1]` — the banner anchors here,
   *  exactly like {@link PinRotateHandle} and {@link PinDirectionHint}. */
  xPct: number;
  yPct: number;
  /** On-screen radius (px at `--pin-scale: 1`) of the rotate knob/cone the
   *  banner must clear — pass the surface's handle radius. */
  radiusPx: number;
  /** The helper copy. Defaults to {@link PIN_DIRECTION_HINT_TEXT}. */
  text?: string;
  /** Force the banner below the pin (used when the pin is near the top edge). */
  below?: boolean;
  /** Horizontal bias so the pill stays on-screen near a side edge. Default
   *  `center`. See {@link bannerPlacement}. */
  align?: BannerAlign;
}

/** Maps the horizontal alignment to the pill's self-translate on X. `start`
 *  pins its left edge to the pin (extends right, away from a left edge); `end`
 *  pins its right edge (extends left); `center` straddles the pin. */
const ALIGN_TRANSLATE_X: Record<BannerAlign, string> = {
  start: "0",
  center: "-50%",
  end: "-100%",
};

/**
 * The helper-text pill, anchored to the pin instead of the screen top so the
 * text, the rotation arrows, and the pin all read in a single glance. It floats
 * just above the coachmark by default (clearing the cone), or flips just below
 * the pin when there is no room above (`below`); near a left/right edge it is
 * biased inward (`align`) so it never hangs off the side.
 *
 * Like the rest of the directional-pin furniture it rides `--pin-scale`, so it
 * holds a constant on-screen size and a constant distance from the pin at any
 * zoom. Purely decorative — `pointer-events-none`, no bearing, never a tap
 * target. Persistent while the surface can set a direction (unlike the transient
 * {@link PinDirectionHint}); the surface mounts/unmounts it on that condition.
 *
 * See [[pin-direction-hint.spec]] and [[directional-pins]].
 */
export function PinDirectionHintBanner({
  xPct,
  yPct,
  radiusPx,
  text = PIN_DIRECTION_HINT_TEXT,
  below = false,
  align = "center",
}: PinDirectionHintBannerProps) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute"
      style={{
        left: `${xPct * 100}%`,
        top: `${yPct * 100}%`,
        width: 0,
        height: 0,
        transform: "scale(var(--pin-scale, 1))",
        transformOrigin: "0 0",
      }}
    >
      <div
        className="absolute"
        style={{
          left: 0,
          // Above: lift the pill's BOTTOM clear of the cone tip. Below: drop its
          // TOP just past the pin marker (the coachmark only ever sits above).
          top: below ? 22 : -(radiusPx + BANNER_GAP_PX),
          transform: `translate(${ALIGN_TRANSLATE_X[align]}, ${below ? "0" : "-100%"})`,
        }}
      >
        {/* Fixed width, not max-width: an absolutely-positioned pill near the
            container's right edge would otherwise shrink-to-fit the narrow space
            there (collapsing to one word per line) BEFORE the leftward `end`
            translate moves it into view. A fixed box lays out at full width
            regardless of position, then the transform places it. */}
        <span className="block w-[15rem] rounded-full bg-black/70 px-4 py-2 text-center text-sm leading-snug text-white shadow-lg">
          {text}
        </span>
      </div>
    </div>
  );
}
